import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { getLocale } from '@/configuration/lang/langSlice';
import { useAppSelector } from '@/configuration/setup/hooks';
import { replacePlaceholders } from '@/utils/stringUtils/replacePlaceholder';

import { releaseNotesData } from './release-notes-content/data';
import { TReleaseNotesProps } from './types';

type SupportedLocale = 'en' | 'fr' | 'de';

const ReleaseNotes = ({ isOpen, onClose }: TReleaseNotesProps) => {
  const intl = useIntl();
  const currentLanguage = useAppSelector(getLocale);
  const locale = (currentLanguage?.slice(0, 2) || 'en') as SupportedLocale;

  const activeReleaseNote = useMemo(() => {
    return {
      ...releaseNotesData,
      content:
        releaseNotesData.content?.map((block) => {
          if (block.type === 'image') {
            return {
              ...block,
              value: replacePlaceholders(block[locale] || block.en, { locale }),
            };
          }

          return block;
        }) || [],
      new_image: releaseNotesData.new_image ? replacePlaceholders(releaseNotesData.new_image, { locale }) : undefined,
      image: releaseNotesData.image ? replacePlaceholders(releaseNotesData.image, { locale }) : undefined,
      video: releaseNotesData.video ? replacePlaceholders(releaseNotesData.video, { locale }) : undefined,
    };
  }, [locale]);

  const footer = (
    <button type="button" className="btn btn-primary btn-outline" onClick={onClose}>
      <span className="text-capitalize">{intl.formatMessage({ id: 'common.releaseNotes.button.label' })}</span>
    </button>
  );

  const body = (
    <div className="padding-x-25 pb-4">
      <div className="display-flex justify-content-between align-items-center margin-y-20">
        {activeReleaseNote.new_image && (
          <div className="margin-b-20">
            <img src={activeReleaseNote.new_image} alt="New release note" className="width-60" />
          </div>
        )}
      </div>

      <div className="release-content">
        {activeReleaseNote.content.map((block, index) => {
          if (block.type === 'markdown') {
            const content = block[locale] || block.en || '';

            return (
              <div key={index} className="margin-b-15">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
              </div>
            );
          }

          if (block.type === 'image') {
            const src = 'value' in block ? block.value : block[locale] || block.en;

            return (
              <div key={index} className="text-center margin-y-20">
                <img src={src} alt={`Illustration ${index}`} className="width-400 border-radius" />
              </div>
            );
          }

          return null;
        })}
      </div>
    </div>
  );

  return (
    <Dialog
      show={isOpen}
      title={intl.formatMessage({ id: 'common.releaseNotes.title' })}
      footer={footer}
      body={body}
      showCloseButton
      useOverflow
      onClose={onClose}
    />
  );
};

export default ReleaseNotes;
